<script lang="ts">
import PrizeCardWide from '@/components/cards/PrizeCardWide.vue'
import GameCardSkeleton from '@/components/cards/new/GameCardSkeleton.vue'
import AppCarousel from '@/components/carousel/AppCarousel.vue'
import CarouselItem from '@/components/carousel/CarouselItem.vue'
import { usePrizesStore } from '@/stores/prizes'
import type { Prize } from '@/types'
import { createDateTimeComparator } from '@/utils/date-time-utils'
import type { TIconValue } from '@/utils/helper-objects'
import { ThemeType } from '@goodlifeplus/themes'
import { defineComponent } from 'vue'

/**
 * MembersPrizesSection
 */
export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
    GameCardSkeleton,
    PrizeCardWide,
  },
  props: {
    columnsPerRow: {
      type: Number,
      default: 4,
    },
  },
  setup() {
    return {
      prizesStore: usePrizesStore(),
    }
  },
  data() {
    return {
      featuredPrizes: [] as Prize[],
    }
  },
  computed: {
    featuredPrizesIcon(): TIconValue {
      return this.$theme.type === ThemeType.Club
        ? this.Icons.Football
        : this.Icons.Entries
    },

    isLoadingPrizes(): boolean {
      return this.prizesStore.isLoading
    },

    hasPrizesError(): boolean {
      return this.prizesStore.hasError && this.prizesStore.prizes.size === 0
    },

    featuredPrizesColumns(): Prize[][] {
      const columns: Prize[][] = []
      const cols = this.columnsPerRow
      const pageSize = cols * 2
      // Group prizes: top row then bottom row, based on visible columns
      for (let page = 0; page < this.featuredPrizes.length; page += pageSize) {
        const pageItems = this.featuredPrizes.slice(page, page + pageSize)
        const topRow = pageItems.slice(0, cols)
        const bottomRow = pageItems.slice(cols, pageSize)
        for (let i = 0; i < topRow.length; i++) {
          const col = [topRow[i]]
          if (bottomRow[i]) {
            col.push(bottomRow[i])
          }
          columns.push(col)
        }
      }
      return columns
    },
  },
  mounted() {
    this.featuredPrizes = this.prizesStore.futurePrizes
  },
})
</script>

<template>
  <AppSection v-if="isLoadingPrizes">
    <div class="mb-4 flex items-center gap-3 sm:gap-4">
      <AppIcon
        :icon="$t('section.featured_prizes.heading_icon')"
        size="xs"
        class="sm:h-8 sm:w-8"
      />
      <h3 class="text-lg font-bold sm:text-2xl">
        {{ $t('section.featured_prizes.heading') }}
      </h3>
    </div>
    <GameCardSkeleton :count="4" />
  </AppSection>
  <AppSection v-else-if="hasPrizesError">
    <div class="py-12 text-center text-slate-600">
      <p class="text-lg">Unable to load prizes right now.</p>
      <p class="mt-2">Please check back later.</p>
    </div>
  </AppSection>
  <AppSection v-else-if="featuredPrizes.length">
    <AppCarousel
      :title="$t('section.featured_prizes.heading')"
      :icon="$t('section.featured_prizes.heading_icon')"
      :view-all-route="{
        name: 'prizes',
        query: { switch: 'featured-prizes' },
      }"
    >
      <CarouselItem
        v-for="(column, index) in featuredPrizesColumns"
        :key="index"
        class="flex w-1/2 flex-col gap-3 @md/main:w-1/3 @lg/main:w-1/4"
      >
        <PrizeCardWide v-for="prize in column" :key="prize.code" :prize />
      </CarouselItem>
    </AppCarousel>
  </AppSection>
</template>

<style lang="scss" scoped></style>
